import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

type ToastVariant = 'default' | 'success' | 'error';

export interface ToastOptions {
  message: string;
  variant?: ToastVariant;
  duration?: number;
  actionLabel?: string;
  actionHref?: string;
}

interface ToastEntry extends ToastOptions {
  id: string;
}

interface ToastContextValue {
  addToast: (toast: ToastOptions) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const TOAST_DEFAULT_DURATION = 4000;

function generateId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timers = useRef<Record<string, number>>({});

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
    const timer = timers.current[id];
    if (timer) {
      window.clearTimeout(timer);
      delete timers.current[id];
    }
  }, []);

  const addToast = useCallback(
    ({ message, variant = 'default', duration = TOAST_DEFAULT_DURATION, actionHref, actionLabel }: ToastOptions) => {
      const id = generateId();
      setToasts((prev) => [...prev, { id, message, variant, duration, actionHref, actionLabel }]);
      if (duration > 0) {
        timers.current[id] = window.setTimeout(() => removeToast(id), duration);
      }
    },
    [removeToast]
  );

  const value = useMemo(() => ({ addToast, removeToast }), [addToast, removeToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-3 max-w-sm">
        {toasts.map((toast) => (
          <div
            key={toast.id}
            className={`rounded-xl border shadow-lg bg-white px-4 py-3 flex items-start gap-3 ${
              toast.variant === 'success'
                ? 'border-emerald-200'
                : toast.variant === 'error'
                  ? 'border-red-200'
                  : 'border-gray-200'
            }`}
          >
            <div className="flex-1 text-sm text-gray-900">{toast.message}</div>
            <div className="flex flex-col items-end gap-2">
              {toast.actionHref && toast.actionLabel && (
                <Link
                  to={toast.actionHref}
                  className="text-sm text-emerald-600 hover:text-emerald-700 font-medium cursor-pointer"
                  onClick={() => removeToast(toast.id)}
                >
                  {toast.actionLabel}
                </Link>
              )}
              <button
                type="button"
                onClick={() => removeToast(toast.id)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Dismiss"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return ctx;
}
